"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.VMDeadCodeInjector = void 0;
const inversify_1 = require("inversify");
const ServiceIdentifiers_1 = require("../container/ServiceIdentifiers");
const IOptions_1 = require("../interfaces/options/IOptions");
const IRandomGenerator_1 = require("../interfaces/utils/IRandomGenerator");
const VMOpCode_1 = require("../enums/vm-transformers/VMOpCode");
let VMDeadCodeInjector = class VMDeadCodeInjector {
    constructor(options, randomGenerator) {
        this.injectionProbability = 0.3;
        this.minInstructionGap = 5;
        this.options = options;
        this.randomGenerator = randomGenerator;
    }
    injectDeadCode(bytecode) {
        if (!this.options.vmDeadCodeInjection) {
            return bytecode;
        }
        if (bytecode.instructions.length < 10) {
            return bytecode;
        }
        const instructions = [...bytecode.instructions];
        const constants = [...bytecode.constants];
        const injectionPoints = this.findInjectionPoints(instructions);
        let injectedCount = 0;
        for (let i = injectionPoints.length - 1; i >= 0; i--) {
            if (this.randomGenerator.getMathRandom() <= this.injectionProbability) {
                const point = injectionPoints[i];
                const deadCodeBlock = this.generateDeadCodeBlock(constants, point);
                instructions.splice(point, 0, ...deadCodeBlock);
                this.adjustJumpTargets(instructions, point, deadCodeBlock.length, point + deadCodeBlock.length);
                injectedCount++;
                if (injectedCount >= 3) {
                    break;
                }
            }
        }
        return {
            ...bytecode,
            instructions,
            constants
        };
    }
    findInjectionPoints(instructions) {
        const points = [];
        let lastPoint = 0;
        for (let i = 1; i < instructions.length - 1; i++) {
            if (i - lastPoint < this.minInstructionGap) {
                continue;
            }
            const prevInstr = instructions[i - 1];
            const currInstr = instructions[i];
            if (this.isJumpInstruction(prevInstr)) {
                continue;
            }
            if (currInstr.op === VMOpCode_1.VMOpCode.RETURN) {
                continue;
            }
            if (prevInstr.op === VMOpCode_1.VMOpCode.TRY_START || currInstr.op === VMOpCode_1.VMOpCode.TRY_END) {
                continue;
            }
            points.push(i);
            lastPoint = i;
        }
        return points;
    }
    generateDeadCodeBlock(constants, insertionPoint) {
        const instructions = [];
        const predicateType = this.randomGenerator.getRandomInteger(0, 2);
        switch (predicateType) {
            case 0:
                this.generateSquareGeZeroPredicate(instructions, constants);
                break;
            case 1:
                this.generateOrOneNotZeroPredicate(instructions, constants);
                break;
            case 2:
                this.generateAndSelfEqualsPredicate(instructions, constants);
                break;
        }
        const deadCodeSize = this.randomGenerator.getRandomInteger(3, 6);
        const jumpOverDeadCode = {
            op: VMOpCode_1.VMOpCode.JUMP_IF_TRUE,
            target: insertionPoint + instructions.length + 1 + deadCodeSize + 1
        };
        instructions.push(jumpOverDeadCode);
        this.generatePlausibleDeadCode(instructions, constants, deadCodeSize);
        return instructions;
    }
    generateSquareGeZeroPredicate(instructions, constants) {
        const randomNum = this.randomGenerator.getRandomInteger(1, 100);
        const constIndex = this.addConstant(constants, randomNum);
        instructions.push({ op: VMOpCode_1.VMOpCode.PUSH_CONST, arg: constIndex });
        instructions.push({ op: VMOpCode_1.VMOpCode.DUP });
        instructions.push({ op: VMOpCode_1.VMOpCode.MUL });
        const zeroIndex = this.addConstant(constants, 0);
        instructions.push({ op: VMOpCode_1.VMOpCode.PUSH_CONST, arg: zeroIndex });
        instructions.push({ op: VMOpCode_1.VMOpCode.GTE });
    }
    generateOrOneNotZeroPredicate(instructions, constants) {
        const randomNum = this.randomGenerator.getRandomInteger(0, 1000);
        const constIndex = this.addConstant(constants, randomNum);
        instructions.push({ op: VMOpCode_1.VMOpCode.PUSH_CONST, arg: constIndex });
        const oneIndex = this.addConstant(constants, 1);
        instructions.push({ op: VMOpCode_1.VMOpCode.PUSH_CONST, arg: oneIndex });
        instructions.push({ op: VMOpCode_1.VMOpCode.BIT_OR });
        const zeroIndex = this.addConstant(constants, 0);
        instructions.push({ op: VMOpCode_1.VMOpCode.PUSH_CONST, arg: zeroIndex });
        instructions.push({ op: VMOpCode_1.VMOpCode.NEQ });
    }
    generateAndSelfEqualsPredicate(instructions, constants) {
        const randomNum = this.randomGenerator.getRandomInteger(1, 100);
        const constIndex = this.addConstant(constants, randomNum);
        instructions.push({ op: VMOpCode_1.VMOpCode.PUSH_CONST, arg: constIndex });
        instructions.push({ op: VMOpCode_1.VMOpCode.DUP });
        instructions.push({ op: VMOpCode_1.VMOpCode.DUP });
        instructions.push({ op: VMOpCode_1.VMOpCode.BIT_AND });
        instructions.push({ op: VMOpCode_1.VMOpCode.EQ });
    }
    generatePlausibleDeadCode(instructions, constants, size) {
        for (let i = 0; i < size; i++) {
            const opType = this.randomGenerator.getRandomInteger(0, 4);
            switch (opType) {
                case 0: {
                    const val = this.randomGenerator.getRandomInteger(0, 100);
                    const idx = this.addConstant(constants, val);
                    instructions.push({ op: VMOpCode_1.VMOpCode.PUSH_CONST, arg: idx });
                    break;
                }
                case 1:
                    if (i > 0) {
                        const ops = [VMOpCode_1.VMOpCode.ADD, VMOpCode_1.VMOpCode.SUB, VMOpCode_1.VMOpCode.MUL];
                        instructions.push({ op: ops[this.randomGenerator.getRandomInteger(0, ops.length - 1)] });
                    }
                    else {
                        instructions.push({ op: VMOpCode_1.VMOpCode.NOP });
                    }
                    break;
                case 2:
                    if (i > 0) {
                        instructions.push({ op: VMOpCode_1.VMOpCode.POP });
                    }
                    else {
                        instructions.push({ op: VMOpCode_1.VMOpCode.NOP });
                    }
                    break;
                case 3:
                    instructions.push({ op: VMOpCode_1.VMOpCode.PUSH_UNDEFINED });
                    break;
                case 4:
                    instructions.push({ op: VMOpCode_1.VMOpCode.NOP });
                    break;
            }
        }
        instructions.push({ op: VMOpCode_1.VMOpCode.POP });
    }
    addConstant(constants, value) {
        const existingIndex = constants.indexOf(value);
        if (existingIndex !== -1) {
            return existingIndex;
        }
        constants.push(value);
        return constants.length - 1;
    }
    isJumpInstruction(instr) {
        return instr.op === VMOpCode_1.VMOpCode.JUMP ||
            instr.op === VMOpCode_1.VMOpCode.JUMP_IF_TRUE ||
            instr.op === VMOpCode_1.VMOpCode.JUMP_IF_FALSE ||
            instr.op === VMOpCode_1.VMOpCode.JUMP_NULLISH ||
            instr.op === VMOpCode_1.VMOpCode.BREAK ||
            instr.op === VMOpCode_1.VMOpCode.CONTINUE;
    }
    adjustJumpTargets(instructions, insertIndex, insertCount, skipUntilIndex) {
        for (let i = 0; i < instructions.length; i++) {
            if (i >= insertIndex && i < skipUntilIndex) {
                continue;
            }
            const instr = instructions[i];
            this.adjustJumpInstructionTarget(instr, insertIndex, insertCount);
            this.adjustTryStartIndices(instr, insertIndex, insertCount);
        }
    }
    adjustJumpInstructionTarget(instr, insertIndex, insertCount) {
        if (this.isJumpInstruction(instr) && 'target' in instr) {
            if (instr.target >= insertIndex) {
                instr.target += insertCount;
            }
        }
    }
    adjustTryStartIndices(instr, insertIndex, insertCount) {
        if (instr.op !== VMOpCode_1.VMOpCode.TRY_START) {
            return;
        }
        const tryInstr = instr;
        if (tryInstr.catchIndex !== undefined && tryInstr.catchIndex >= insertIndex) {
            tryInstr.catchIndex += insertCount;
        }
        if (tryInstr.finallyIndex !== undefined && tryInstr.finallyIndex >= insertIndex) {
            tryInstr.finallyIndex += insertCount;
        }
        if (tryInstr.endIndex >= insertIndex) {
            tryInstr.endIndex += insertCount;
        }
    }
};
exports.VMDeadCodeInjector = VMDeadCodeInjector;
exports.VMDeadCodeInjector = VMDeadCodeInjector = __decorate([
    (0, inversify_1.injectable)(),
    __param(0, (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IOptions)),
    __param(1, (0, inversify_1.inject)(ServiceIdentifiers_1.ServiceIdentifiers.IRandomGenerator)),
    __metadata("design:paramtypes", [typeof (_a = typeof IOptions_1.IOptions !== "undefined" && IOptions_1.IOptions) === "function" ? _a : Object, typeof (_b = typeof IRandomGenerator_1.IRandomGenerator !== "undefined" && IRandomGenerator_1.IRandomGenerator) === "function" ? _b : Object])
], VMDeadCodeInjector);
